// Full Tailwind-styled Client Discovery Survey form in React
import React, { useState } from 'react';

const ClientDiscoverySurvey = () => {
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage('');

    // Required fields including consent
    const requiredFields = [
      'companyName', 'contactName', 'email', 'businessModel', 'northStar',
      'problem', 'opportunity', 'consent'
    ];
    for (const field of requiredFields) {
      if (!formData[field]) {
        setMessage(`Please fill out required field: ${field}`);
        setMessageType('error');
        setSubmitting(false);
        return;
      }
    }

    const payload = { ...formData, client: formData.contactName };
    try {
      const response = await fetch(
        'https://ov6cnw6rh4q3ps7pvxpr37aseq0hejrd.lambda-url.us-east-1.on.aws/',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      );
      const result = await response.json();

      if (response.ok) {
        setMessage('Survey submitted successfully');
        setMessageType('success');
        setFormData({}); // reset form
      } else {
        setMessage(result.message || 'Submission failed');
        setMessageType('error');
      }
    } catch (err) {
      console.error(err);
      setMessage('Network error while submitting survey.');
      setMessageType('error');
    } finally {
      setSubmitting(false);
    }
  };

  const renderInput = (name, label, required = false, type = 'text', placeholder = '', helper = '') => (
    <div>
      <label htmlFor={name} className="block text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        id={name}
        name={name}
        value={formData[name] || ''}
        onChange={handleChange}
        placeholder={placeholder}
        required={required}
        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-4 py-2"
      />
      {helper && <p className="mt-1 text-xs text-gray-500">{helper}</p>}
    </div>
  );

  const renderTextarea = (name, label, required = false, rows = 3, helper = '') => (
    <div>
      <label htmlFor={name} className="block text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <textarea
        id={name}
        name={name}
        rows={rows}
        value={formData[name] || ''}
        onChange={handleChange}
        required={required}
        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-4 py-2"
      />
      {helper && <p className="mt-1 text-xs text-gray-500">{helper}</p>}
    </div>
  );

  const renderSelect = (name, label, options = [], required = false, helper = '') => (
    <div>
      <label htmlFor={name} className="block text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select
        id={name}
        name={name}
        value={formData[name] || ''}
        onChange={handleChange}
        required={required}
        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-4 py-2"
      >
        <option value="" disabled>Select an option</option>
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      {helper && <p className="mt-1 text-xs text-gray-500">{helper}</p>}
    </div>
  );

  return (
    <section className="py-12 bg-white sm:py-16 lg:py-20">
      <div className="px-4 mx-auto sm:px-6 lg:px-8 max-w-7xl">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-3xl font-bold text-center text-gray-900 mb-10">Client Discovery Survey</h1>

          {/* Message Bar */}
          {message && (
            <div
              className={`mb-6 px-4 py-3 rounded-md text-sm font-medium ${
                messageType === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}
              role="alert"
            >
              {message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            <fieldset className="space-y-6">
              <legend className="text-xl font-semibold text-gray-800 mb-4">1. Context & Orientation</legend>
              {renderInput('companyName', 'Company Name', true, 'text', '', 'Enter the full legal name of your company.')}
              {renderSelect('companySize', 'Size of Company', [
                { value: '1-10', label: '1–10 employees' },
                { value: '11-50', label: '11–50 employees' },
                { value: '51-200', label: '51–200 employees' },
                { value: '201-500', label: '201–500 employees' },
                { value: '501-1000', label: '501–1000 employees' },
                { value: '1001+', label: '1001+ employees' }
              ], false, 'Select the employee range that best describes your organization.')}
              {renderInput('contactName', 'Primary Contact Name', true)}
              {renderInput('email', 'Email Address', true, 'email', '', "We'll use this to send you the survey results.")}
              {renderInput('budget', 'Estimated Budget', false, 'text', '$0 - $', 'Provide your approximate budget in USD.')}
              {renderTextarea('businessModel', 'Business Model (How do you generate revenue?)', true, 3)}
              {renderTextarea('northStar', 'North-Star Objective (12–24 months)', true, 3)}
              {renderTextarea('stakeholders', 'Key Stakeholders & Decision-Makers')}
              {renderTextarea('successMetrics', 'Current Success Metrics / KPIs')}
            </fieldset>

            <fieldset className="space-y-6">
              <legend className="text-xl font-semibold text-gray-800 mb-4">2. Product / Service Deep-Dive</legend>
              {renderTextarea('problem', 'Problem Articulation', true, 3)}
              {renderTextarea('coreFeatures', 'Core Features (Must-Have)')}
              {renderTextarea('enablingFeatures', 'Enabling / Nice-to-Have Features')}
              {renderTextarea('workarounds', 'Hidden Workarounds Customers Use Today')}
              {renderTextarea('differentiation', 'Key Differentiation (Why you vs. alternatives?)')}
            </fieldset>

            <fieldset className="space-y-6">
              <legend className="text-xl font-semibold text-gray-800 mb-4">3. Niche & Segment Mapping</legend>
              {renderTextarea('verticals', 'Primary Industry / Vertical(s)')}
              {renderTextarea('roles', 'Key User Roles / Personas')}
              {renderTextarea('geography', 'Geography (location-based segmentation)', false, 2, 'Specify target regions, countries, or cities.')}
              {renderSelect('valueTier', 'Value Tier & Willingness to Pay', [
                { value: 'premium', label: 'High – Premium' },
                { value: 'standard', label: 'Medium – Standard' },
                { value: 'budget', label: 'Low – Budget-Sensitive' },
                { value: 'unknown', label: 'Unsure' }
              ])}
            </fieldset>

            <fieldset className="space-y-6">
              <legend className="text-xl font-semibold text-gray-800 mb-4">4. Evidence & Insight Harvest</legend>
              {renderTextarea('interviews', 'Customer Interview Highlights')}
              {renderTextarea('salesThemes', 'Sales / Support Call Themes')}
              {renderTextarea('competitive', 'Competitive Teardown Insights')}
              {renderTextarea('dataDip', 'Usage / Funnel Data Insights')}
            </fieldset>

            <fieldset className="space-y-6">
              <legend className="text-xl font-semibold text-gray-800 mb-4">5. Synthesis & Direction</legend>
              {renderTextarea('opportunity', 'Opportunity Statement', true)}
              {renderTextarea('roadmap', 'Preliminary Roadmap (Now / Next / Later)')}
              {renderTextarea('successScorecard', 'Success Scorecard (Top 3–5 Metrics)')}
            </fieldset>

            <fieldset className="space-y-4">
              <legend className="text-xl font-semibold text-gray-800 mb-2">Consent</legend>
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  name="consent"
                  onChange={handleChange}
                  className="form-checkbox h-4 w-4 text-indigo-600"
                  required
                />
                <span className="ml-2 text-sm text-gray-700">I consent to the collection and processing of the information provided above.</span>
              </label>
            </fieldset>

            <div>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center w-full px-6 py-3 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-md shadow"
              >
                {submitting ? 'Submitting…' : 'Submit Survey'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ClientDiscoverySurvey;
